/**
 * Alpitour Rybbit Analytics Utilities
 *
 * Centralizes Rybbit event helpers for the Alpitour (AT) site.
 * All events automatically include package_type from the store.
 *
 * Usage:
 *   import { alpitour_rybbit_event, slim_search_info, slim_room_info } from "@alpitour/utilities/rybbit"
 *   alpitour_rybbit_event("package:search:submit", { ...slim_search_info(search_params), result_count: 12 })
 */

import Store from "@/vuex/store";
import { get_object_field } from "@utilities";
import { track_form_event } from "@utilities/integrations/rybbit";

// ---------------------------------------------------------------------------
//  Core event emitter
// ---------------------------------------------------------------------------

/**
 * Send a Rybbit event with package_type automatically merged.
 * Extra props override defaults in case of key collision.
 *
 * Facade over `track_form_event` (Layer 1): it auto-injects `package_type`
 * as page-level context. Behavior is identical to the previous
 * `rybbit_event(eventName, { package_type, ...props })` for callers, but
 * payloads now go through the 2048-char safety net.
 *
 * @param {string} eventName - Colon-separated event name (e.g. "package:search:submit")
 * @param {Object} [props={}] - Event-specific properties
 */
export const alpitour_rybbit_event = (eventName, props) => {
  let package_type = "";
  try {
    package_type = Store.state.liveat.package_type || "";
  } catch (e) {
    // Store may not be ready yet
  }

  track_form_event({
    event: eventName,
    data: props && typeof props === "object" ? props : {},
    context: { package_type },
  });
};

// ---------------------------------------------------------------------------
//  Slim info helpers — extract filterable dimensions from data objects
// ---------------------------------------------------------------------------

/**
 * Extract slim search dimensions from search_params.
 * Works for both flight-and-stay and only-stay flows.
 *
 * @param {Object} search_params - The search_params computed from booking-engine
 * @returns {Object} Flat props suitable for Rybbit payload
 */
export const slim_search_info = (search_params) => {
  if (!search_params) return {};

  const occupancy = search_params.occupancy || {};
  const adults = occupancy.adults || 0;
  const children = Array.isArray(occupancy.children) ? occupancy.children : [];

  return {
    destination: search_params.location_label || "",
    keyd: search_params.keyd || "",
    departure_date: search_params.departure_date || "",
    checkin_date: search_params.checkin_date || "",
    checkout_date: search_params.checkout_date || "",
    nights: search_params.nights || 0,
    adults: adults,
    children: children.length,
  };
};

/**
 * Extract slim room dimensions from a selected_room object.
 * Accepts the raw selected_room from either flow:
 *   - flight-and-stay: { quote_payload: { roomDescription, roomTrattDescription, totAmount, idSolution } }
 *   - only-stay: { rate: { description, name, solution: { id }, pricing: { total } } }
 *
 * @param {Object} selected_room - The selected_room object from the store or component
 * @returns {Object} Flat props suitable for Rybbit payload
 */
export const slim_room_info = (selected_room) => {
  if (!selected_room) return {};

  const room_description = get_object_field(selected_room, "quote_payload.roomDescription")
    || get_object_field(selected_room, "rate.name");
  const treatment = get_object_field(selected_room, "quote_payload.roomTrattDescription")
    || get_object_field(selected_room, "rate.description");
  const price_fas = get_object_field(selected_room, "quote_payload.totAmount", null);
  const price = price_fas != null ? price_fas : get_object_field(selected_room, "rate.pricing.total", 0);
  const solution_id = get_object_field(selected_room, "quote_payload.idSolution")
    || get_object_field(selected_room, "rate.solution.id");

  return {
    room_description: room_description,
    treatment: treatment,
    price: Number(price) || 0,
    solution_id: solution_id,
  };
};

/**
 * Extract slim detail params for mismatch/error events.
 *
 * @param {Object} detail_params - The detail_params from store
 * @param {boolean} is_only_stay - Whether the current flow is only-stay
 * @returns {Object} Flat props suitable for Rybbit payload
 */
export const slim_detail_params = (detail_params, is_only_stay) => {
  if (!detail_params) return {};

  return {
    ext_id: detail_params.ext_id || "",
    pid: detail_params.pid || "",
    solution_id: detail_params.solution_id || "",
    has_ext_id: detail_params.ext_id ? 1 : 0,
    has_pid: detail_params.pid ? 1 : 0,
    has_solution_id: detail_params.solution_id ? 1 : 0,
    expected_flow: is_only_stay ? "only_stay" : "flight_and_stay",
  };
};
